import type { ConversationSummary } from '../types/chat.types';

/**
 * Picks which conversations the sidebar should render.
 *
 * `/db/user-conversations` is paginated server-side now (see
 * `ChatContext.refreshHistory`/`loadMoreHistory`), so `conversations` is
 * already bounded by how many pages the user has explicitly loaded — no
 * client-side re-cap needed here (there used to be one, back when the API had
 * no limit at all and would return everything in one response — see
 * .claude-notes/FRONTEND_PENDIENTES.md §2.2).
 *
 * - No query: every loaded conversation (the input is assumed pre-sorted
 *   newest-first, as `chatHistoryService.listConversations()` returns it).
 * - A query: every conversation whose name matches — but this only searches
 *   what's already loaded, not the full backend history, since the paginated
 *   endpoint has no search/filter parameter of its own.
 * - The currently-open conversation is always included, even if it doesn't
 *   match the query, so opening an older chat never makes it disappear from
 *   its own sidebar.
 */
export function selectVisibleConversations(
  conversations: ConversationSummary[],
  query: string,
  activeConversationId?: string | null,
): ConversationSummary[] {
  const trimmed = query.trim().toLowerCase();

  const selected = trimmed
    ? conversations.filter((conversation) => conversation.name.toLowerCase().includes(trimmed))
    : conversations;

  if (!activeConversationId || selected.some((c) => c.conversationId === activeConversationId)) {
    return selected;
  }

  const active = conversations.find((c) => c.conversationId === activeConversationId);
  if (!active) {
    return selected;
  }

  // Re-merge and re-sort rather than just prepending: `selected` is already
  // newest-first, and splicing the active conversation in at its real
  // position keeps that invariant instead of forcing it to the top.
  return [...selected, active].sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1));
}
